import { Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { GrpcLanguage, MobileDeviceType } from 'src/app/core/data-models/identity.data.models';
import { UserDataService } from 'src/app/core/data-services/identity/user.data.service';
import { FundDataService } from 'src/app/core/data-services/fund/fund.data.service';
import { environment } from 'src/environments/environment';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss']
})
export class OtpInputComponent implements OnInit, OnChanges, OnDestroy {
  otpForm!: FormGroup;
  otp = '';
  /** Matches backend `Otp:MaxTimeoutSpan` (2 minutes). */
  private readonly otpExpiryMinutes = 2;
  minutes = this.otpExpiryMinutes;
  seconds = 0;
  intervalId: any;

  @Input() phoneNumber!: string;
  /** Parent already names where the code was sent (for example an email address). */
  @Input() hideDestination = false;
  @Input() generateNewOtp: boolean = true;
  @Input() autoValidate: boolean = true;
  @Input() showOtpMsg: boolean = true;
  @Input() operationType: number = 1; // 👈 added operationType as input (login = 1, register = 2, etc.)
  /** When set, OTP is sent to the fund authorized signatory (not the logged-in applicant). */
  @Input() signingFundId: string | null = null;
  /** When set by a parent, prefills the four boxes (optional; loan repayment uses the same `GenerateUserOTP` path as login). */
  @Input() externalOtpPrefill: string | null = null;
  /** When true, "resend" only emits `resendRequested`; the parent re-sends the OTP (e.g. investor login). */
  @Input() externalResend: boolean = false;
  @Output() onValidationSuccess = new EventEmitter<string>();
  @Output() onValidationFailed = new EventEmitter<string>();
  @Output() resendRequested = new EventEmitter<void>();

  constructor(
    private userDataService: UserDataService,
    private fundDataService: FundDataService,
    private notificationService: NotificationService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    if (!this.signingFundId && !(this.phoneNumber && this.phoneNumber.trim() !== '')) {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      this.phoneNumber = user?.phoneNumber || '';
    }

    this.otpForm = new FormGroup({
      otp1: new FormControl('', [
        Validators.required,
        Validators.maxLength(1),
        Validators.pattern(/^[0-9]$/),
      ]),
      otp2: new FormControl('', [
        Validators.required,
        Validators.maxLength(1),
        Validators.pattern(/^[0-9]$/),
      ]),
      otp3: new FormControl('', [
        Validators.required,
        Validators.maxLength(1),
        Validators.pattern(/^[0-9]$/),
      ]),
      otp4: new FormControl('', [
        Validators.required,
        Validators.maxLength(1),
        Validators.pattern(/^[0-9]$/),
      ]),
    });

    this.intervalId = setInterval(() => this.tick(), 1000);

    if (this.generateNewOtp) {
      this.generateUserOtp();
    } else if (!environment.production) {
      this.getOtpForDev();
    }

    this.applyExternalOtpPrefill();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalOtpPrefill'] && this.otpForm) {
      this.applyExternalOtpPrefill();
    }
  }

  /** Prefills from parent when backend returns a dev OTP (e.g. SendPayLoanOtp with OtpSms:IsMock). */
  private applyExternalOtpPrefill(): void {
    if (!this.otpForm) {
      return;
    }
    const digits = String(this.externalOtpPrefill ?? '').replace(/\D/g, '');
    if (digits.length < 4) {
      return;
    }
    this.otpForm.patchValue({
      otp1: digits[0],
      otp2: digits[1],
      otp3: digits[2],
      otp4: digits[3],
    });
  }

  /** Prefills the four OTP boxes when a dev/mock code is available (non-production only). */
  private applyDevOtpIfNonProduction(otp: string | null | undefined): void {
    if (!otp || environment.production) {
      return;
    }
    const digits = String(otp).replace(/\D/g, '');
    if (digits.length < 4) {
      return;
    }
    this.otpForm.patchValue({
      otp1: digits[0],
      otp2: digits[1],
      otp3: digits[2],
      otp4: digits[3],
    });
  }

  /** Toast + prefill the signing OTP when portal `production` is false. */
  private toastAndPrefillDevOtp(otp: string | null | undefined): void {
    if (!otp || environment.production) {
      return;
    }
    const digits = String(otp).replace(/\D/g, '');
    if (digits.length < 4) {
      return;
    }
    const msg =
      this.languageService.currentLanguage === 'en'
        ? ` OTP: ${digits.slice(0, 4)}`
        : `رمز التحقق : ${digits.slice(0, 4)}`;
    this.notificationService.success(msg);
    this.applyDevOtpIfNonProduction(digits);
  }

  private generateUserOtp() {
    if (this.signingFundId) {
      this.generateSigningOtp();
      return;
    }
    this.userDataService.GenerateUserOTP({
      phoneNumber: this.phoneNumber,
      operationType: this.operationType
    }).subscribe({
      next: (res) => {
        if (res?.data?.errorCode == null) {
          const msg =
            this.languageService.currentLanguage === 'en'
              ? 'OTP sent successfully'
              : 'تم إرسال رمز التحقق بنجاح';
          this.notificationService.success(msg);
          this.applyDevOtpIfNonProduction((res as any)?.devOtp);
        } else {
          this.notificationService.error(res.message || 'Failed to send OTP');
        }
      },
      error: (error) => {
        let msg = this.languageService.currentLanguage == 'en'
          ? 'Something went wrong'
          : 'يوجد خطأ ما';
        this.notificationService.error(error?.error?.message ?? msg);
        // window.location.href = '#/authentication/login';
        
      }
    });
  }

  private generateSigningOtp() {
    this.fundDataService.generateBorrowerSigningOtp(this.signingFundId!).subscribe({
      next: (res) => {
        const payload = res?.resource ?? res?.data;
        const last4 = payload?.mobileLast4 || payload?.MobileLast4;
        if (last4) {
          this.phoneNumber = last4;
        }
        const msg =
          this.languageService.currentLanguage === 'en'
            ? 'OTP sent to the authorized signatory'
            : 'تم إرسال رمز التحقق إلى المخول بالتوقيع';
        this.notificationService.success(payload?.message || res?.message || msg);
        const devOtp =
          (res as any)?.devOtp ||
          (res as any)?.DevOtp ||
          payload?.devOtp ||
          payload?.DevOtp;
        this.toastAndPrefillDevOtp(devOtp);
      },
      error: (error) => {
        const fallback = this.languageService.currentLanguage == 'en'
          ? 'Something went wrong'
          : 'يوجد خطأ ما';
        this.notificationService.error(error?.error?.message ?? fallback);
      }
    });
  }

  /** Non-production only: fetches the latest OTP for `phoneNumber`, toasts and prefills it. Parents using `externalResend` call this after re-sending. */
  getOtpForDev() {
    this.userDataService.GetUserDevOTP({
      phoneNumber: this.phoneNumber
    }).subscribe({
      next: (res) => {
        const otp = UserDataService.extractOtpFromGetUserOtpResponse(res);
        if (!otp || environment.production) {
          return;
        }
        const msg =
          this.languageService.currentLanguage === 'en' ? `OTP: ${otp}` : `رمز التحقق: ${otp}`;
        this.notificationService.success(msg);
        this.applyDevOtpIfNonProduction(otp);
      },
    });
  }

  verifyOTP() {
    const otp =
      this.otpForm.get('otp1')?.value +
      this.otpForm.get('otp2')?.value +
      this.otpForm.get('otp3')?.value +
      this.otpForm.get('otp4')?.value;
    // this.notificationService.success('OTP verified');
     
    if (!this.otpForm.valid) {
      this.notificationService.error('Please enter a valid OTP');
      return;
    }

    if (this.autoValidate) {
      this.onValidationSuccess.emit(otp);
      return;
    }

    this.userDataService.ValidateUserOTP(otp, this.phoneNumber, GrpcLanguage.En).subscribe({
      next: (res) => {
        if (res?.success == true) {
          this.notificationService.success(res?.message || 'OTP verified');
          this.onValidationSuccess.emit(otp);
        } else {
          this.notificationService.error(res?.message || 'OTP invalid');
          this.onValidationFailed.emit(otp);
        }
      },
      error: (error) => {
          this.notificationService.error(error?.message || 'OTP verified');
          this.onValidationFailed.emit(otp);
      }
    });
  }

  resendOtp() {
    this.minutes = this.otpExpiryMinutes;
    this.seconds = 0;
    clearInterval(this.intervalId);
    this.intervalId = setInterval(() => this.tick(), 1000);

    if (this.signingFundId) {
      this.generateSigningOtp();
      return;
    }

    if (this.externalResend) {
      this.resendRequested.emit();
      return;
    }

    this.userDataService.GenerateUserOTP({
      phoneNumber: this.phoneNumber,
      operationType: this.operationType
    }).subscribe({
      next: (res) => {
        if (res?.data?.errorCode == null) {
          this.notificationService.success(res.data?.message || 'OTP resent');
          this.applyDevOtpIfNonProduction((res as any)?.devOtp);
        } else {
          this.notificationService.error(res.data?.message || 'Failed to resend OTP');
        }
      },
    });
  }

  private tick() {
    if (this.seconds === 0) {
      if (this.minutes === 0) {
        clearInterval(this.intervalId);
      } else {
        this.minutes--;
        this.seconds = 59;
      }
    } else {
      this.seconds--;
    }
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
  }
keytab1() {
  let value = this.otpForm.get('otp1')?.value;

  if (!this.onlyDigits(value)) {
    this.otpForm.get('otp1')?.setValue('');
    return;
  }

  if (value !== '') {
    document.getElementById("otp2")!.focus();
  }
}

keytab2() {
  let value = this.otpForm.get('otp2')?.value;

  if (!this.onlyDigits(value)) {
    this.otpForm.get('otp2')?.setValue('');
    return;
  }

  if (value !== '') {
    document.getElementById("otp3")!.focus();
  }
}

keytab3() {
  let value = this.otpForm.get('otp3')?.value;

  if (!this.onlyDigits(value)) {
    this.otpForm.get('otp3')?.setValue('');
    return;
  }

  if (value !== '') {
    document.getElementById("otp4")!.focus();
  }
}
keytab4() {
  let value = this.otpForm.get('otp4')?.value;

  if (!this.onlyDigits(value)) {
    this.otpForm.get('otp4')?.setValue('');
    return;
  }
}

onlyDigits(value: string): boolean {
  return /^[0-9]$/.test(value); 
}



  keyBackspace(event: KeyboardEvent, currentInputId: string, previousInputId: string | null) {
    if (event.key === 'Backspace') {
      const currentInput = this.otpForm.get(currentInputId);
      if (currentInput?.value === '' && previousInputId) {
        document.getElementById(previousInputId)?.focus();
        this.otpForm.get(previousInputId)?.setValue('');
      }
    }
  }
}
